import { Flow_Model_Enum } from "gql/api";
import { extrapolateTVDByMD } from "../deviationSurvey";
import type { DeviationSurveyEntryRows } from "@/composables/scenarioCalculations/calculationTypes";
import { computePressureFromDepthPsia } from "@/utils/computations";
import {
  getWellFlowingPressureUsingComposite,
  getMaxFlowRateComposite
} from "./composite";
import {
  getWellFlowingPressureUsingVogelRate,
  getWellFlowingPressureUsingVogelModifiedRate,
  getMaxFlowRateVogel,
  getMaxFlowRateVogelModified
} from "./vogel";
import { getWellFlowingPressureUsingWiggins } from "./wiggins";
import {
  getWellFlowingPressureUsingPi,
  getMaxFlowRatePI
} from "./productivityIndex";

export const getWellFlowingPressure = (
  flowModel: string,
  desiredLiquidRateSTDB: number,
  wellTestTotalLiquidRateSTBD: number,
  reservoirPressure: number,
  wellTestProducingBottomHolePressurePSIA: number,
  bubblePointPressurePSIA: number,
  waterCutCalculated: number,
  productivityIndex: number | null = null
): number => {
  if (flowModel === Flow_Model_Enum.Composite) {
    return getWellFlowingPressureUsingComposite(
      desiredLiquidRateSTDB,
      wellTestProducingBottomHolePressurePSIA,
      reservoirPressure,
      bubblePointPressurePSIA,
      wellTestTotalLiquidRateSTBD,
      waterCutCalculated
    );
  } else if (flowModel === Flow_Model_Enum.Vogel) {
    return getWellFlowingPressureUsingVogelRate(
      reservoirPressure,
      wellTestProducingBottomHolePressurePSIA,
      wellTestTotalLiquidRateSTBD,
      desiredLiquidRateSTDB,
      bubblePointPressurePSIA,
      waterCutCalculated
    );
  } else if (flowModel === Flow_Model_Enum.VogelModified) {
    return getWellFlowingPressureUsingVogelModifiedRate(
      reservoirPressure,
      wellTestProducingBottomHolePressurePSIA,
      wellTestTotalLiquidRateSTBD,
      desiredLiquidRateSTDB,
      bubblePointPressurePSIA,
      waterCutCalculated
    );
  } else if (flowModel === Flow_Model_Enum.Wiggins) {
    return getWellFlowingPressureUsingWiggins(
      desiredLiquidRateSTDB,
      wellTestTotalLiquidRateSTBD,
      reservoirPressure,
      wellTestProducingBottomHolePressurePSIA || 0.01,
      waterCutCalculated
    );
  } else {
    // Straight
    return getWellFlowingPressureUsingPi(
      wellTestTotalLiquidRateSTBD,
      reservoirPressure,
      wellTestProducingBottomHolePressurePSIA,
      desiredLiquidRateSTDB,
      productivityIndex
    );
  }
};

export const getMaxFlowRateBBLD = (
  flowModel: string,
  // This pressure is used for the chart intervals
  desiredWellFlowingPressurePSIA: number,
  wellFlowingPressureTestPSIA: number,
  reservoirPressurePSIA: number,
  bubblePointPressurePSIA: number,
  liquidFlowRateTestSTBD: number,
  waterCutRatio: number,
  productivityIndex: number | null = null
) => {
  if (flowModel === Flow_Model_Enum.Composite) {
    return getMaxFlowRateComposite(
      desiredWellFlowingPressurePSIA,
      wellFlowingPressureTestPSIA,
      reservoirPressurePSIA,
      bubblePointPressurePSIA,
      liquidFlowRateTestSTBD,
      waterCutRatio
    );
  } else if (flowModel === Flow_Model_Enum.Vogel) {
    return getMaxFlowRateVogel(
      reservoirPressurePSIA,
      wellFlowingPressureTestPSIA,
      desiredWellFlowingPressurePSIA,
      liquidFlowRateTestSTBD,
      bubblePointPressurePSIA,
      waterCutRatio
    );
  } else if (flowModel === Flow_Model_Enum.VogelModified) {
    return getMaxFlowRateVogelModified(
      reservoirPressurePSIA,
      wellFlowingPressureTestPSIA,
      desiredWellFlowingPressurePSIA,
      liquidFlowRateTestSTBD,
      bubblePointPressurePSIA,
      waterCutRatio
    );
  } else {
    // Straight
    return getMaxFlowRatePI(
      reservoirPressurePSIA,
      wellFlowingPressureTestPSIA,
      desiredWellFlowingPressurePSIA,
      liquidFlowRateTestSTBD,
      productivityIndex
    );
  }
};

// Assuming _wellProperties and entryRows are accessible in the current context
// and their types are properly defined elsewhere in your application
// returns well_flowing_pressure psia
// CURRENTLY THIS IS JUST HYDROSTATIC PRESSURE
export function getWellPressureAtDepth(
  referenceTrueVerticalDepthFt: number,
  referenceFluidDensityLBMFT3: number,
  referencePressurePSIA: number,
  entryRows: DeviationSurveyEntryRows | undefined,
  targetMeasuredDepthFt?: number | null,
  targetTrueVerticalDepthFt?: number | null
): number {
  if (!entryRows) {
    return 0;
  }
  if (!targetTrueVerticalDepthFt) {
    targetTrueVerticalDepthFt = extrapolateTVDByMD(
      targetMeasuredDepthFt ?? 0,
      entryRows
    );
  }
  // if (targetTrueVerticalDepthFt === referenceTrueVerticalDepthFt) {
  //   return referencePressurePSIA;
  // }
  const pressureDiff = computePressureFromDepthPsia(
    referenceTrueVerticalDepthFt,
    targetTrueVerticalDepthFt,
    referenceFluidDensityLBMFT3
  );
  return referencePressurePSIA - pressureDiff;
}
